import React from "react";
import {
  DocumentType,
  downloadRegistrationDocument,
  getRegistrationDocumentContent,
  getRegistrationDocumentsForRegistrationId,
} from "../../Base/Services/RegistrationDocumentService";
import { IdentificationStatus } from "../../Base/AdoptionConstants";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@material-ui/core";
import useAlerts from "../../Base/AlertProvider";
import { CheckCircleOutline, RemoveCircleOutline, GetApp, Visibility, Close } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import { updateRegistrationIdentificationStatus } from "../../Base/Services/RegistrationService";
import useAdminContext from "../AdminContextProvider";
import ButtonWithLoadingSpinner from "../../Base/Components/ButtonWithLoadingSpinner";

export default function RegistrationIdentityVerification({ registration, setRegistration }) {
  const { addErrorAlert } = useAlerts();
  const [documentToPreview, setDocumentToPreview] = React.useState();
  const [imagePreviewContent, setImagePreviewContent] = React.useState();
  const [processingUpdate, setProcessingUpdate] = React.useState();

  const [idDocuments, setIdDocuments] = React.useState(null);
  const identificationStatus = React.useMemo(
    () => IdentificationStatus[registration.identificationStatus],
    [registration]
  );
  const adminContext = useAdminContext();
  // If registrationId provided, then try to load the existing identity document(s)
  React.useEffect(() => {
    if (registration.registrationId) {
      getRegistrationDocumentsForRegistrationId(registration.registrationId, [DocumentType.IDENTIFICATION])
        .then(setIdDocuments)
        .catch(() => addErrorAlert("There was an error loading ID documents"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registration.registrationId]);

  function previewDocument(imageDocument) {
    setDocumentToPreview(imageDocument);
    getRegistrationDocumentContent(imageDocument)
      .then(setImagePreviewContent)
      .catch(() => addErrorAlert("Error loading image"));
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <Typography variant="body1">
            <b style={{ marginRight: 4 }}>Status:</b>
          </Typography>
          <Typography variant="body1" style={{ display: "flex", justifyContent: "start", alignItems: "center" }}>
            <span id="dialog-identification-status" style={{ marginRight: 2 }}>
              {identificationStatus.label}
            </span>
            {identificationStatus === IdentificationStatus.VERIFIED ? (
              <CheckCircleOutline style={{ color: "green" }} />
            ) : (
              <RemoveCircleOutline />
            )}
          </Typography>
        </div>
        {identificationStatus !== IdentificationStatus.VERIFIED && (
          <ButtonWithLoadingSpinner
            color="primary"
            variant="contained"
            isLoading={processingUpdate}
            onClick={() => {
              setProcessingUpdate(true);
              updateRegistrationIdentificationStatus(registration.registrationId, IdentificationStatus.VERIFIED.value)
                .then(setRegistration)
                .then(adminContext.triggerDataResync)
                .catch(() => addErrorAlert("There was an error completing the operation"))
                .finally(() => setProcessingUpdate(false));
            }}
          >
            Verify Identity
          </ButtonWithLoadingSpinner>
        )}
        {identificationStatus === IdentificationStatus.VERIFIED && idDocuments && (
          <ButtonWithLoadingSpinner
            color="default"
            variant="outlined"
            isLoading={processingUpdate}
            onClick={() => {
              setProcessingUpdate(true);
              // If the registrant hasn't uploaded ID documents, assume they had chosen to mail them
              const statusToUpdateTo =
                idDocuments.length > 0
                  ? IdentificationStatus.PENDING_REVIEW_UPLOAD
                  : IdentificationStatus.PENDING_REVIEW_MAIL;
              updateRegistrationIdentificationStatus(registration.registrationId, statusToUpdateTo.value)
                .then(setRegistration)
                .then(adminContext.triggerDataResync)
                .catch(() => addErrorAlert("There was an error completing the operation"))
                .finally(() => setProcessingUpdate(false));
            }}
          >
            Revoke Identity Verification
          </ButtonWithLoadingSpinner>
        )}
      </div>
      <div id="id-documents-container" style={{ marginTop: 16 }}>
        {idDocuments ? (
          idDocuments.length > 0 ? (
            <List style={{ width: 275 }}>
              {idDocuments.map((doc, i) => (
                <ListItem key={i}>
                  <ListItemText>{doc.documentName}</ListItemText>
                  <ListItemIcon>
                    <Tooltip title="Download">
                      <IconButton
                        aria-label="download"
                        onClick={() => {
                          downloadRegistrationDocument(doc);
                        }}
                      >
                        <GetApp />
                      </IconButton>
                    </Tooltip>
                  </ListItemIcon>
                  {(doc.documentContentType.startsWith("image/") || doc.documentContentType === "application/pdf") && (
                    <ListItemIcon>
                      <Tooltip title="Preview">
                        <IconButton onClick={() => previewDocument(doc)} aria-label="preview">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </ListItemIcon>
                  )}
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body1">No ID Documents found.</Typography>
          )
        ) : (
          <Skeleton />
        )}
        <Dialog open={Boolean(documentToPreview)} onClose={() => setDocumentToPreview(null)} fullWidth maxWidth="lg">
          {documentToPreview && (
            <>
              <DialogTitle>
                <span style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Image Preview: {documentToPreview.documentName}</span>
                  <IconButton size="small" color="default" onClick={() => setDocumentToPreview(null)}>
                    <Close />
                  </IconButton>
                </span>
              </DialogTitle>
              <DialogContent style={{ textAlign: "center" }}>
                {imagePreviewContent ? (
                  documentToPreview.documentContentType === "application/pdf" ? (
                    <embed
                      src={`data:${documentToPreview.documentContentType};base64,${imagePreviewContent}`}
                      style={{ minHeight: 600, width: "100%" }}
                    />
                  ) : (
                    <img
                      alt={documentToPreview?.documentName}
                      src={`data:${documentToPreview.documentContentType};base64,${imagePreviewContent}`}
                    />
                  )
                ) : (
                  <Skeleton
                    variant="rect"
                    style={{ height: 450, width: 600, marginLeft: "auto", marginRight: "auto" }}
                  />
                )}
              </DialogContent>
            </>
          )}
        </Dialog>
      </div>
    </div>
  );
}
